import { createSlice } from '@reduxjs/toolkit';
// utils
import { callApi } from '../../utils/apiCall';
import { errorHandler } from '../../utils/errorHandler';
import { hasFeatureAccess } from '../../utils/featurePermission';
//
import { dispatch } from '../store';
import { updateError } from './user';
import { setFacebookAdAccount } from './chat';
import { updateDentallyEnabledGlobal } from './practice';

// Extract a readable error message from an API response
const extractErrorMessage = (data, fallback = 'Something went wrong') => {
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    return data?.detail || data?.message || data?.error || fallback;
};

// ---------------------------------------------------------------------
const initialState = {
    isLoading: false,
    practiceUser: {
        results: []
    },
    inviteUser: [],
    message: '',
    error: '',
    // ALWAYS an array. It used to start as `false` and double as a
    // "not fetched yet" sentinel, which meant every consumer had to know
    // that `false`, `true` (StripeAuthorized dispatched a bare boolean on a
    // successful connect) and `[]` were all possible — some did their own
    // `Array.isArray()` dance and the rest just assumed. Load state now
    // lives in `stripeLoadState`, so the shape never varies.
    stripeConnected: [],
    stripePracticeId: null,
    stripeRequestId: null,
    stripeLoadState: 'idle', // idle | loading | loaded | forbidden | error
    stripeDisconnecting: false,
    facebookConnected: false,
    dentallyConfigs: null,
    dentallyLoading: false,
    dentallyConfigPracticeId: null,
    dentallyConfigRequestId: null,
    dentallyConfigLoadState: 'not_requested',
};

const slice = createSlice({
    name: 'practiceIntegration',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
            state.error = '';
            state.message = '';
        },
        stripeRequestStarted(state, { payload }) {
            if (String(state.stripePracticeId) !== String(payload.practiceId)) state.stripeConnected = [];
            state.stripePracticeId = payload.practiceId;
            state.stripeRequestId = payload.requestId;
            state.stripeLoadState = 'loading';
        },
        stripeRequestFinished(state, { payload }) {
            if (state.stripeRequestId !== payload.requestId || String(state.stripePracticeId) !== String(payload.practiceId)) return;
            if (payload.items) state.stripeConnected = payload.items;
            state.stripeLoadState = payload.loadState;
        },
        stripeConnectedSlice(state, action) {
            // Normalise here rather than trusting callers — a non-array
            // payload is a bug at the call site, and silently storing it is
            // how `stripeConnected[0].stripe_account_preference` became a
            // crash on the integrations page.
            state.stripeConnected = Array.isArray(action.payload) ? action.payload : [];
            state.stripeLoadState = 'loaded';
        },
        stripeLoadStateSlice(state, action) {
            state.stripeLoadState = action.payload;
        },
        stripeDisconnectingSlice(state, action) {
            state.stripeDisconnecting = action.payload;
        },
        facebookConnectedSlice(state, action) {
            state.facebookConnected = action.payload;
        },
        dentallyConfigsSlice(state, action) {
            state.dentallyConfigs = action.payload;
        },
        dentallyLoadingSlice(state, action) {
            state.dentallyLoading = action.payload;
        },
        dentallyConfigRequestStarted(state, action) {
            state.dentallyConfigPracticeId = action.payload.practiceId;
            state.dentallyConfigRequestId = action.payload.requestId;
            state.dentallyConfigs = null;
            state.dentallyLoading = true;
            state.dentallyConfigLoadState = 'loading';
        },
        dentallyConfigRequestResolved(state, action) {
            if (
                String(state.dentallyConfigPracticeId) !== String(action.payload.practiceId)
                || state.dentallyConfigRequestId !== action.payload.requestId
            ) return;
            state.dentallyConfigs = action.payload.data;
            state.dentallyConfigLoadState = action.payload.loadState || 'loaded';
        },
        dentallyConfigRequestFinished(state, action) {
            if (
                String(state.dentallyConfigPracticeId) !== String(action.payload.practiceId)
                || state.dentallyConfigRequestId !== action.payload.requestId
            ) return;
            state.dentallyLoading = false;
        },
    }
});

export const {
    stripeConnectedSlice,
    stripeLoadStateSlice,
    stripeDisconnectingSlice,
    reset,
    facebookConnectedSlice,
    dentallyConfigsSlice,
    dentallyLoadingSlice,
    dentallyConfigRequestStarted,
    dentallyConfigRequestResolved,
    dentallyConfigRequestFinished,
} = slice.actions;

export default slice.reducer;

// `silent` (default false) suppresses the error snackbar on a non-200/403
// response or network failure — used by background readiness probes
// (CreateSendPaymentLinkDialog / PaymentCard's Resend menu) that fetch this
// on every open/mount purely to pre-flight-disable a channel; a transient
// failure there shouldn't interrupt the user with an unrelated toast. The
// pre-existing callers (PaymentOption, CreateStripePaymentPage,
// practiceIntegration settings page) don't pass it, so their error toast is
// unchanged.
let stripeRequestSequence = 0;

export function getStripe(practiceId, { silent = false } = {}) {
    return async (_dispatch, getState) => {
        const isCurrent = () => {
            const current = getState?.()?.practiceTreatmentList?.practiceId;
            return current == null || String(current) === String(practiceId);
        };
        if (!practiceId || !isCurrent()) return;
        stripeRequestSequence += 1;
        const requestId = stripeRequestSequence;
        dispatch(slice.actions.stripeRequestStarted({ practiceId, requestId }));
        const finish = (loadState, items) => dispatch(slice.actions.stripeRequestFinished({ practiceId, requestId, loadState, items }));
        if (!hasFeatureAccess('payment-processing', practiceId)) {
            finish('forbidden', []);
            return;
        }
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/stripe/`, 'GET', null, false, { bustCache: true });
            if (requestId !== stripeRequestSequence || !isCurrent()) return;
            if (response.status === 200) finish('loaded', response.data?.results || []);
            else if (response.status === 403) finish('forbidden', []);
            else {
                finish('error');
                if (!silent) errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            if (requestId !== stripeRequestSequence || !isCurrent()) return;
            finish('error');
            if (!silent) dispatch(updateError({ message: 'Could not load Stripe accounts. Please try again.', success: false }));
        }
    };
}

// The connected account, or null. Every read of the Stripe account should
// go through here: it is the one place that knows the list may be empty.
const stripeScopeIsCurrent = (state) => {
    const selected = state.practiceTreatmentList?.practiceId;
    const loaded = state.practiceIntegration?.stripePracticeId;
    return selected == null || loaded == null || String(selected) === String(loaded);
};
export const selectStripeAccount = (state) => {
    if (!stripeScopeIsCurrent(state)) return null;
    const accounts = state.practiceIntegration?.stripeConnected;
    return (Array.isArray(accounts) && accounts[0]) || null;
};

// Connected AND usable. `is_enabled: false` is a disconnected account whose
// history we keep — the backend refuses to create payment links on it
// (apps/stripe/payment_links.py `_validate_stripe`), so the UI must not
// offer them either. Older API responses carry no `is_enabled`; treat a
// missing flag as enabled so this cannot misread an un-upgraded backend.
export const selectStripeIsLive = (state) => {
    const account = selectStripeAccount(state);
    return Boolean(account && account.is_enabled !== false);
};

// Have we actually asked the server yet? `[]` means "no account"; it must
// not be rendered as such before the first response lands.
export const selectStripeLoaded = (state) =>
    stripeScopeIsCurrent(state) && ['loaded', 'forbidden', 'error'].includes(state.practiceIntegration?.stripeLoadState);

// `true` / `false` / `undefined` until we have asked. This is the shape
// `getPaymentLinkChannelReadiness` documents for `stripeConnected`, where
// undefined means "unknown, don't block yet".
//
// Callers used to derive the same thing from `Array.isArray(accounts)`,
// which worked only by accident: the slice's initial value was the
// non-array `false`, so "not an array" happened to coincide with "not
// fetched". Now that the value is always an array, that inference reads an
// empty list as a confirmed "no Stripe account" and blocks the send buttons
// before the probe has run. Load state answers the question directly.
export const selectStripeIsLiveOrPending = (state) =>
    (selectStripeLoaded(state) ? selectStripeIsLive(state) : undefined);

// Disconnect keeps the account row, its preferences and every payment on
// it. It disables the account, expires any still-payable checkout session
// and hands the OAuth grant back to Stripe, so the practice can connect a
// different account cleanly. `onDone` receives { success, data }.
export function disconnectStripe(
    practiceId,
    accountId,
    { reason = '', deauthorize = true, onDone } = {}
) {
    return async () => {
        if (!hasFeatureAccess('payment-processing')) return;
        dispatch(slice.actions.stripeDisconnectingSlice(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/stripe/${accountId}/disconnect/`,
                'post',
                { reason, deauthorize }
            );
            if (response.status === 200) {
                // The local disconnect always succeeds; the Stripe-side
                // revoke is best effort. Saying "Stripe disconnected" when
                // the grant is still live would leave the practice believing
                // they had withdrawn an authorisation they had not. An empty
                // `deauthorize_error` means the revoke was deliberately
                // skipped, not that it failed.
                const revokeFailed = Boolean(response.data?.deauthorize_error);
                dispatch(updateError({
                    success: !revokeFailed,
                    message: revokeFailed
                        ? 'Disconnected here, but Stripe would not confirm the authorisation was removed. Disconnect again to retry, or remove it from the Stripe dashboard.'
                        : 'Stripe disconnected',
                }));
                dispatch(getStripe(practiceId));
                if (onDone) onDone({ success: true, data: response.data });
            } else {
                const message = extractErrorMessage(response?.data, 'Could not disconnect Stripe');
                dispatch(updateError({ success: false, message }));
                if (onDone) onDone({ success: false, message });
            }
        } catch (err) {
            const message = err.message || 'Something went wrong';
            dispatch(updateError({ success: false, message }));
            if (onDone) onDone({ success: false, message });
        } finally {
            dispatch(slice.actions.stripeDisconnectingSlice(false));
        }
    };
}

export function stripeUrl(practiceId, { isCurrent = () => true } = {}) {
    return async () => {
        if (!practiceId) return false;
        try {
            const response = await callApi(`/api/v1/stripe/auth_url/?practice_id=${encodeURIComponent(practiceId)}`, 'GET', null, false, { bustCache: true });
            if (!isCurrent()) return false;
            const url = response.status === 200 && response.data?.auth_url ? new URL(response.data.auth_url) : null;
            if (url?.protocol === 'https:' && url.hostname === 'connect.stripe.com' && String(response.data.practice_id) === String(practiceId)) {
                window.location.assign(url.href);
                return true;
            }
            dispatch(updateError({ success: false, message: 'Could not start Stripe connection. Check your access and try again.' }));
        } catch (err) {
            if (isCurrent()) dispatch(updateError({ message: 'Could not start Stripe connection. Please try again.', success: false }));
        }
        return false;
    };
}

export function stripePreferenceUrl(data, practiceId, id) {
    return async () => {
        if (!hasFeatureAccess('payment-processing')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/stripe/${id}/`, 'put', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Stripe preference url connected successfully' }));
            } else {
                dispatch(updateError({ success: false, message: response.data?.stripe_account_preference?.checkout_cancel_url?.toString() || response.data?.stripe_account_preference?.checkout_success_url?.toString() || 'Something went wrong' }));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

// Keep settled results briefly: React remounts must not spend a single-use
// OAuth code or replacement token again. Credentials never enter Redux actions.
const stripeCallbackRequests = new Map();
export function stripeCallback(data, onResult, identity = 'session') {
    return async () => {
        const key = JSON.stringify([identity, data]);
        const now = Date.now();
        stripeCallbackRequests.forEach((entry, entryKey) => {
            if (now - entry.at > 600000) stripeCallbackRequests.delete(entryKey);
        });
        let entry = stripeCallbackRequests.get(key);
        if (!entry) {
            const promise = (async () => {
                try {
                    const response = await callApi('/api/v1/stripe/callback/', 'post', data, false, { suppressServerErrorDispatch: true });
                    if (response.status === 200) return { success: true, message: 'Stripe connected successfully', data: response.data };
                    if (response.status === 409 && response.data?.code === 'stripe_account_mismatch') return { success: false, mismatch: response.data };
                    return { success: false, retryable: false, message: 'Stripe connection could not be completed. Return to integrations and start again.' };
                } catch {
                    return { success: false, retryable: false, message: 'We could not confirm the Stripe connection. The request may have completed. Check the connection in integrations before starting again.' };
                }
            })();
            entry = { at: now, promise };
            if (stripeCallbackRequests.size >= 32) stripeCallbackRequests.delete(stripeCallbackRequests.keys().next().value);
            stripeCallbackRequests.set(key, entry);
        }
        const result = await entry.promise;
        if (onResult) onResult(result);
        return result;
    };
}

export function getFacebook(practiceId) {
    return async () => {
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/`);
            if (response.status === 200) {
                dispatch(slice.actions.facebookConnectedSlice(response.data.results));
            } else if (response.status !== 403) {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function facebookUrl(practiceId) {
    return async () => {
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/connect/`);
            if (response.status === 200) {
                window.location.href = response.data.auth_url;
            } else {
                dispatch(updateError({ success: true, message: response.data.message || 'Something went wrong' }));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function tiktokUrl(practiceId) {
    return async () => {
        if (!hasFeatureAccess('tiktok-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tiktok-accounts/connect/`);
            if (response.status === 200) {
                window.location.href = response.data.auth_url;
            } else {
                dispatch(updateError({ success: true, message: response.data.message || 'Something went wrong' }));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        }
    };
}

export function facebookCallback(practiceId, data, handlesuccess) {
    return async () => {
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/callback/`, 'post', data);
            if (response.status === 200) {
                handlesuccess({ success: true, message: 'Facebook connected successfully' });
            } else {
                handlesuccess({ success: false, message: extractErrorMessage(response?.data) });
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Somthing went wrong', success: false }));
        }
    };
}

export function deleteFacebookAccount(practiceId, id, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('facebook-integration')) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/facebook-accounts/${id}/disconnect/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Account disconnected successfully!' }));
                handleSuccess({ success: true });
                dispatch(setFacebookAdAccount(''));
            } else {
                dispatch(updateError({ success: false, message: extractErrorMessage(response?.data) }));
                handleSuccess({ success: false, message: extractErrorMessage(response?.data) });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error?.message || 'Something went wrong' }));
        }
    };
}

let dentallyConfigRequestSequence = 0;
// Practices with a /dentally/ GET in flight right now. Several components read
// useDentallyIntegrationScope in the same commit, so without this each of them
// would fire its own probe before the first one had flipped the load state.
const dentallyConfigInFlight = new Set();

export function getDentallyConfigs(practiceId) {
    return async () => {
        // Pass practiceId so the gate refuses stale-practice fires during a
        // practice-switch window (otherwise the bootstrap-safe path lets the
        // call through and the BE returns 403 — DENTALSEM-CRM-1BS3 regression).
        if (!hasFeatureAccess('dentally-integration', practiceId)) return;
        const inFlightKey = String(practiceId);
        if (dentallyConfigInFlight.has(inFlightKey)) return;
        dentallyConfigInFlight.add(inFlightKey);
        dentallyConfigRequestSequence += 1;
        const requestId = dentallyConfigRequestSequence;
        try {
            dispatch(slice.actions.dentallyConfigRequestStarted({ practiceId, requestId }));
            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/`);
            if (response.status === 200) {
                dispatch(slice.actions.dentallyConfigRequestResolved({ practiceId, requestId, data: response.data, loadState: 'loaded' }));
                // Same source of truth for the app-global flag as the config list,
                // so the two can never disagree about whether Dentally is on for
                // this practice. (checkDentallyEnabled derives it identically.)
                dispatch(updateDentallyEnabledGlobal((response.data?.results || []).some((config) => config?.is_active === true)));
            } else if (response.status !== 403) {
                dispatch(slice.actions.dentallyConfigRequestResolved({ practiceId, requestId, data: null, loadState: 'error' }));
                dispatch(updateDentallyEnabledGlobal(false));
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            } else {
                dispatch(slice.actions.dentallyConfigRequestResolved({ practiceId, requestId, data: null, loadState: 'forbidden' }));
                dispatch(updateDentallyEnabledGlobal(false));
            }
        } catch (err) {
            dispatch(slice.actions.dentallyConfigRequestResolved({ practiceId, requestId, data: null, loadState: 'error' }));
            dispatch(updateDentallyEnabledGlobal(false));
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dentallyConfigInFlight.delete(inFlightKey);
            dispatch(slice.actions.dentallyConfigRequestFinished({ practiceId, requestId }));
        }
    };
}

// `onError(responseData)` receives the 400 body so callers can surface field errors
// (e.g. `{ site_id: ["This site does not exist in the connected Dentally account."] }`).
export function updateDentallyConfig(practiceId, configId, data, onSuccess, onError) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration', practiceId)) return;
        try {
            dispatch(slice.actions.dentallyLoadingSlice(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/${configId}/`, 'PATCH', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Dentally configuration updated successfully' }));
                if (onSuccess) onSuccess(response.data);
                dispatch(updateDentallyEnabledGlobal(response.data.is_active));
                dispatch(getDentallyConfigs(practiceId));
            } else {
                if (typeof onError === 'function') onError(response.data, response.status);
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.dentallyLoadingSlice(false));
        }
    };
}

export function createDentallyConfig(practiceId, data, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration', practiceId)) return;
        try {
            dispatch(slice.actions.dentallyLoadingSlice(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/`, 'POST', data);
            if (response.status === 200 || response.status === 201) {
                dispatch(updateError({ success: true, message: 'Dentally connected successfully' }));
                if (onSuccess) onSuccess(response.data);
                dispatch(getDentallyConfigs(practiceId));
            } else {
                errorHandler(response.data, (payload) => dispatch(updateError(payload)));
            }
        } catch (err) {
            dispatch(updateError({ message: err.message || 'Something went wrong', success: false }));
        } finally {
            dispatch(slice.actions.dentallyLoadingSlice(false));
        }
    };
}

export function validateDentallyToken(practiceId, onResult) {
    return async () => {
        if (!hasFeatureAccess('dentally-integration', practiceId)) {
            if (onResult) onResult(false);
            return;
        }
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/dentally/validate_token/`);
            if (response.status === 200) {
                if (onResult) onResult(response.data?.valid === true);
            } else if (onResult) {
                onResult(false);
            }
        } catch (err) {
            if (onResult) onResult(false);
        }
    };
}
